import { DatasheetFormatListDto } from "@common/dto/format/datasheet-list-dto";
import {
  getDatasheetFolderFormats as getDatasheetFolderFormatsAction,
  GetDatasheetFolderFormats as CommonGetDatasheetFolderFormats,
  getTdsFolderFormats as getTdsFolderFormatsAction,
} from "@common/actions/formats";
import { GetTdsFolderFormats as CommonGetTdsFolderFormats } from "../components/datasheet-format-management/datasheet-format-management.types";
import client, { clientTransformed } from "@common/utils/rest";
import {
  RENAME_FORMAT_ENDPOINTS,
} from "../constants/api-endpoints";

export type GetDatasheetFolderFormats = CommonGetDatasheetFolderFormats;
export type GetTdsFolderFormats = CommonGetTdsFolderFormats;

/**
 * Load datasheet formats for a given folder/context.
 * Thin wrapper around the shared formats action.
 */
export const getDatasheetResultFolderFormats = (
  params: GetDatasheetFolderFormats
): Promise<Array<DatasheetFormatListDto>> => {
  return getDatasheetFolderFormatsAction(params);
};

/**
 * Load TDS (table datasheet) formats for a given folder/context/tableId.
 * Thin wrapper around the shared formats action.
 */
export const getTdsResultFolderFormats = (
  params: GetTdsFolderFormats
): Promise<Array<DatasheetFormatListDto>> => {
  return getTdsFolderFormatsAction(params);
};

/**
 * Delete a datasheet format.
 */
export const deleteDatasheetFormat = async (formatId: number, tds?: boolean) => {
  const path = tds ? `api/formats/tds/${formatId}/delete` : `api/formats/ds/${formatId}/delete`;
  const response = await client({
    path,
    method: "POST",
  });
  return response.entity;
};

export const loadFormatDefaultsSettings = async (params: {
  formatId: number;
  context: number;
  tds?: boolean;
}) => {
  const tdsParam = params.tds !== undefined ? `&isTDS=${params.tds}` : '';
  const response = await client({
    path: `api/datasheet/loadDSDefaults?formatId=${params.formatId}&context=${params.context}${tdsParam}`,
    method: "GET",
  });
  return response.entity;
};

/**
 * Load recently used datasheet formats for the current user.
 * Context: 1 = Individual (IDS), 2 = Pedigree (PDS).
 */
export const loadDSMostRecent = async (context: number = 1): Promise<Array<{ id: number; name: string; folderid: number }>> => {
  const response = await clientTransformed({
    path: `api/datasheet/loadDSMostRecent?context=${context}`,
    method: "GET",
  });
  return response.entity ?? [];
};

/**
 * Save the selected format as last used (updates MRU list).
 * Context: 1 = Individual (IDS), 2 = Pedigree (PDS).
 */
export const saveLastUsedFormat = async (formatId: number, context: number = 1): Promise<void> => {
  await client({
    path: `api/datasheet/saveLastUsedFormat?formatId=${formatId}&context=${context}`,
    method: "POST",
  });
};

/**
 * Rename a datasheet format.
 */
export const renameDatasheetFormat = async (params: {
  formatId: number;
  newFormatName: string;
  folderId: number;
  tds?: boolean;
  tableId?: number;
}) => {
  const path = params.tds ? RENAME_FORMAT_ENDPOINTS.TDS : RENAME_FORMAT_ENDPOINTS.DS;
  const entity: {
    formatName: string;
    formatId: number;
    tableId?: number;
  } = {
    formatName: params.newFormatName,
    formatId: params.formatId,
  };
  
  if (params.tds && params.tableId) {
    entity.tableId = params.tableId;
  }
  
  const response = await client({
    path,
    method: "POST",
    entity,
  });
  return response.entity;
};